<script lang="ts">
	import type { AuthorPage } from '$models/Page/AuthorPage';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { sanitizeHtml } from '$lib/utils';

	let { page }: { page: AuthorPage } = $props();

	const authorSocials = $derived(
		page.linkedinUrl ? [{ id: 'linkedin', url: page.linkedinUrl, title: 'LinkedIn' }] : []
	);
</script>

<div class="author-hero">
	<div class="author-hero-wrapper">
		<div class="author-hero-main">
			{#if page.image}
				<figure class="author-hero-visual">
					<img
						class="author-hero-image"
						src={page.image.url}
						srcset={getWordPressImageSrcSet(page.image)}
						sizes="(min-width: 768px) 320px, 100vw"
						alt={page.image.alt}
						width={page.image.width}
						height={page.image.height}
						loading="eager"
						decoding="async"
					/>
				</figure>
			{/if}
			<div class="author-hero-inner">
				<div class="author-hero-header">
					{#if page.jobTitle || page.companyName}
						<p class="author-hero-subtitle">
							{[page.jobTitle, page.companyName].filter(Boolean).join(' · ')}
						</p>
					{/if}
					<h1 class="author-hero-title">
						{page.title}
					</h1>
				</div>
				{#if page.summary}
					<div class="author-hero-content rich-text">
						{@html sanitizeHtml(page.summary)}
					</div>
				{/if}
			</div>
		</div>
		{#if authorSocials.length > 0 || page.knowledgeAreas.length > 0}
			<div class="author-hero-footer">
				{#if authorSocials.length > 0}
					<div class="author-hero-socials">
						<div class="author-hero-socials-items">
							{#each authorSocials as item}
								<a
									class="author-hero-socials-item author-hero-socials-item--{item.id}"
									href={item.url}
									target="_blank"
									title={item.title}><span class="screenreader">{item.title}</span></a
								>
							{/each}
						</div>
					</div>
				{/if}
				{#if page.knowledgeAreas.length > 0}
					<ul class="author-hero-knowledge-areas">
						{#each page.knowledgeAreas as area (area.slug)}
							<li class="author-hero-knowledge-area">{area.name}</li>
						{/each}
					</ul>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style lang="postcss">
	.author-hero {
		&-wrapper {
			display: flex;
			flex-direction: column;
			padding: 24px;
			background-color: var(--color-lungo-forte);
			border-radius: 16px;
			overflow: hidden;
		}

		&-main {
			position: relative;
			display: flex;
			flex-direction: column;
			gap: 16px;
			padding-bottom: 24px;

			&::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}
		}

		&-visual {
			flex-shrink: 0;
			margin: 0;
			width: 100%;
			max-width: 400px;
		}

		&-inner {
			width: 100%;
		}

		&-image {
			width: 100%;
			height: auto;
			border-radius: var(--radius-3);
			display: block;
		}

		&-header {
			margin-bottom: 8px;
		}

		&-subtitle {
			font-size: 12px;
		}

		&-title {
			font-size: 32px;
			line-height: 1;
		}

		&-content {
			font-size: 18px;
			margin-bottom: 24px;
			max-width: 680px;
		}

		&-socials {
			position: relative;
			padding-block: 24px;

			&-items {
				display: flex;
				align-items: center;
				gap: 10px;
			}

			&-item {
				width: 32px;
				height: 32px;
				display: block;
				background-position: center center;
				background-repeat: no-repeat;
				background-size: 26px;

				&--linkedin {
					background-image: url('data:image/svg+xml;base64,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');
				}
			}

			&::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}
		}

		&-knowledge-areas {
			position: relative;
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			list-style: none;
			padding: 0;
			padding-block: 24px;

			&::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}
		}

		&-knowledge-area {
			padding: 4px 12px;
			border-radius: 8px;
			background: white;
			font-size: var(--font-size-0);
		}

		@media (--md) {
			&-wrapper {
				padding: 40px;
			}

			&-main {
				flex-direction: row-reverse;
				padding: 24px 0;
				gap: 40px;

				&:before {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					height: 2px;
					background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
					background-size: 5px 2px;
					background-repeat: repeat-x;
					pointer-events: none;
				}
			}

			&-visual {
				max-width: 260px;
			}

			&-title {
				font-size: 40px;
			}

			&-knowledge-areas,
			&-socials {
				padding: 0;

				&::after {
					display: none;
				}
			}

			&-footer {
				display: flex;
				justify-content: space-between;
				align-items: center;
				gap: 16px;
				padding-top: 24px;
			}
		}

		@media (--lg) {
			&-wrapper {
				padding: 40px 60px;
			}

			&-header {
				margin-bottom: 16px;
			}

			&-title {
				font-size: 48px;
			}

			&-subtitle {
				font-size: 14px;
			}
		}
	}
</style>
